import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  AlertTriangle, CalendarDays, Download, FileArchive, Inbox, Loader2, PoundSterling, Receipt, Trash2, UserRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  AdminPage, PageHeader, Panel, SettingRow, adminButton, adminInput, useAdminPageTitle,
} from '@/components/admin/ui';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import {
  ALL_DATASETS, useDataExport, useDeleteOrganization, useOrgDeletionRequest,
} from '@/hooks/useDataExport';
import type { ExportDataset } from '@/lib/dataExport';
import { cn } from '@/lib/utils';

/** The platform's own house business can never be deleted (DB trigger agrees). */
export const FOUNDER_ORG_ID = 'a90138ee-26a5-4570-97b0-5a1144b9ad85';

const DATASETS: { key: ExportDataset; title: string; description: string; icon: typeof UserRound; ownerOnly?: boolean }[] = [
  { key: 'customers', title: 'Customers', description: 'Names, contact details and addresses.', icon: UserRound },
  { key: 'bookings', title: 'Bookings', description: 'Every job with its date, service, status and assigned cleaners.', icon: CalendarDays },
  { key: 'enquiries', title: 'Enquiries', description: 'Quote requests and enquiries from your website.', icon: Inbox },
  { key: 'invoices', title: 'Invoices', description: 'Totals, what’s been paid and what’s still outstanding.', icon: Receipt, ownerOnly: true },
  { key: 'services', title: 'Services', description: 'Your services and prices, including hidden ones.', icon: PoundSterling },
];

function formatDay(value: string | null | undefined) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

const normalise = (value: string) => value.trim().toLowerCase();

const DataAdmin = () => {
  useAdminPageTitle('Your data');
  const navigate = useNavigate();
  const { orgId, org, canWrite, loading } = useAdminOrg();
  const { currentOrgRole, staffRole, isPlatformAdmin, signOut } = useAuth();
  const { toast } = useToast();
  const exporter = useDataExport();
  const deleter = useDeleteOrganization();
  const { data: deletion } = useOrgDeletionRequest(orgId);
  const [pending, setPending] = useState<ExportDataset | 'all' | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // The role in the business being viewed decides; staffRole is a fallback for owners without a membership row.
  const isOwner = currentOrgRole ? currentOrgRole === 'owner' : staffRole === 'owner';
  const canExport = Boolean(orgId) && !isPlatformAdmin && (isOwner || staffRole === 'supervisor');
  const datasets = DATASETS.filter((d) => isOwner || !d.ownerOnly);
  const orgName = org?.name ?? '';
  const isFounderOrg = orgId === FOUNDER_ORG_ID;

  const confirmSchema = z.object({
    confirmName: z
      .string()
      .refine((value) => orgName.length > 0 && normalise(value) === normalise(orgName), {
        message: 'Type your business name exactly as shown.',
      }),
  });
  const form = useForm<z.infer<typeof confirmSchema>>({
    resolver: zodResolver(confirmSchema),
    defaultValues: { confirmName: '' },
    mode: 'onChange',
  });
  const typed = form.watch('confirmName');
  const nameMatches = orgName.length > 0 && normalise(typed ?? '') === normalise(orgName);

  const runExport = async (dataset: ExportDataset | 'all') => {
    if (!orgId || !canExport) return;
    setPending(dataset);
    try {
      await exporter.mutateAsync({
        orgId,
        orgName,
        dataset,
        include: ALL_DATASETS.filter((d) => datasets.some((x) => x.key === d)),
      });
      toast({ title: 'Download ready', description: 'Check your downloads folder.' });
    } catch (error) {
      toast({ title: 'Could not export your data', description: (error as Error).message, variant: 'destructive' });
    } finally {
      setPending(null);
    }
  };

  const onDelete = async (values: { confirmName: string }) => {
    if (!orgId || !isOwner || !canWrite || isFounderOrg) return;
    try {
      const result = await deleter.mutateAsync({ organizationId: orgId, confirmName: values.confirmName });
      setConfirmOpen(false);
      toast({
        title: 'Your business has been deleted',
        description: `Your website is offline now. Everything will be permanently removed on ${formatDay(result.purgeAfter)}.`,
      });
      await signOut();
      navigate('/', { replace: true });
    } catch (error) {
      toast({ title: 'Could not delete your business', description: (error as Error).message, variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const downloadAllButton = (
    <Button
      size="sm"
      className={adminButton.primary}
      disabled={!canExport || exporter.isPending}
      onClick={() => runExport('all')}
    >
      {pending === 'all' ? <Loader2 className="animate-spin" /> : <FileArchive />}
      Download everything
    </Button>
  );

  return (
    <AdminPage width="narrow">
      <PageHeader description="Download a copy of your business data whenever you like, or delete your business." />

      {deletion && (
        <div
          role="status"
          data-testid="deletion-scheduled"
          className="flex items-start gap-2.5 rounded-xl border border-destructive/30 bg-destructive/[0.06] p-4 text-[13px] text-destructive"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            This business is being deleted. Your website is offline, and everything will be permanently removed on{' '}
            <strong>{formatDay(deletion.purge_after)}</strong>. You can still download your data until then. Changed your
            mind? Contact support before that date.
          </p>
        </div>
      )}

      <Panel
        title="Download your data"
        description="CSV files open in Excel, Numbers or Google Sheets. Available on every plan."
        actions={downloadAllButton}
      >
        {!canExport ? (
          <p className="text-[13px] text-muted-foreground">
            {isPlatformAdmin
              ? 'Exports are made by the business’s own owner or managers.'
              : 'Ask the business owner or a manager to download your data.'}
          </p>
        ) : (
          <div className="adm-divide -my-3.5">
            {datasets.map(({ key, title, description, icon: Icon }) => (
              <SettingRow
                key={key}
                icon={<Icon />}
                title={title}
                description={description}
                control={
                  <Button
                    size="sm"
                    variant="ghost"
                    className={adminButton.outline}
                    disabled={exporter.isPending}
                    onClick={() => runExport(key)}
                    aria-label={`Download ${title} CSV`}
                  >
                    {pending === key ? <Loader2 className="animate-spin" /> : <Download />}
                    CSV
                  </Button>
                }
              />
            ))}
          </div>
        )}
      </Panel>

      {isOwner && !isPlatformAdmin && !deletion && (
        <Panel
          title={<span className="text-destructive">Delete my business</span>}
          icon={<Trash2 className="h-4 w-4 text-destructive" />}
          description="Close your account and remove your business data."
          className="border-destructive/30"
          data-testid="danger-zone"
        >
          {isFounderOrg ? (
            <p className="text-[13px] text-muted-foreground">
              This is the platform’s own business, so it can’t be deleted here.
            </p>
          ) : (
            <div className="space-y-4">
              <ul className="list-disc space-y-1.5 pl-5 text-[13px] leading-relaxed text-muted-foreground">
                <li>Your website and booking page go offline straight away, and you’ll be signed out.</li>
                <li>Any plan subscription is cancelled, so you won’t be charged again.</li>
                <li>
                  Your customers, bookings, enquiries, invoices, team and website are permanently deleted after 30 days.
                  Until then, contact support if you change your mind.
                </li>
                <li>Download everything first if you want to keep a copy.</li>
              </ul>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  className={adminButton.danger}
                  disabled={!canWrite}
                  onClick={() => {
                    form.reset({ confirmName: '' });
                    setConfirmOpen(true);
                  }}
                >
                  <Trash2 /> Delete my business…
                </Button>
              </div>
            </div>
          )}
        </Panel>
      )}

      <Dialog open={confirmOpen} onOpenChange={(open) => !deleter.isPending && setConfirmOpen(open)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete {orgName}?</DialogTitle>
            <DialogDescription>
              Your website goes offline now and all your data is permanently deleted after 30 days. Any plan subscription
              is cancelled.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-xl border border-border bg-muted/40 p-3 text-[13px] text-muted-foreground">
            <p>Want a copy first?</p>
            <Button
              size="sm"
              variant="ghost"
              className={cn(adminButton.outline, 'mt-2')}
              disabled={exporter.isPending}
              onClick={() => runExport('all')}
            >
              {pending === 'all' ? <Loader2 className="animate-spin" /> : <FileArchive />}
              Download everything first
            </Button>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onDelete)} className="space-y-4">
              <FormField
                control={form.control}
                name="confirmName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Type <span className="font-semibold text-foreground">{orgName}</span> to confirm
                    </FormLabel>
                    <FormControl>
                      <Input autoComplete="off" spellCheck={false} className={adminInput} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter className="gap-2 sm:gap-0">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className={adminButton.outline}
                  disabled={deleter.isPending}
                  onClick={() => setConfirmOpen(false)}
                >
                  Keep my business
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  variant="destructive"
                  className="h-9 gap-1.5 rounded-lg px-3.5 text-[13px] font-semibold [&_svg]:size-4"
                  disabled={!nameMatches || deleter.isPending}
                >
                  {deleter.isPending ? <Loader2 className="animate-spin" /> : <Trash2 />}
                  Delete my business
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
};

export default DataAdmin;
